import { useEffect, useRef } from 'react';

export function useCanvasAnimation(
  draw: (ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => void,
  options?: {
    width?: number;
    height?: number;
    pixelRatio?: number;
  }
): React.RefObject<HTMLCanvasElement> {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawRef = useRef(draw);

  // Sync draw function reference on every render
  useEffect(() => {
    drawRef.current = draw;
  }, [draw]);

  const rafIdRef = useRef<number | null>(null);
  const totalTimeRef = useRef<number>(0);
  const lastTimeRef = useRef<number | null>(null);

  const isIntersectingRef = useRef<boolean>(false);
  const containerWidthRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const parent = canvas.parentElement;
    if (!parent) return;

    const pixelRatio = options?.pixelRatio ?? Math.min(window.devicePixelRatio || 1, 2);
    const height = options?.height ?? 340;

    const updateDimensions = (width: number) => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      canvas.width = width * pixelRatio;
      canvas.height = height * pixelRatio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      ctx.scale(pixelRatio, pixelRatio);
    };

    // Initialize width
    const initialWidth = options?.width ?? parent.clientWidth;
    containerWidthRef.current = initialWidth;
    updateDimensions(initialWidth);

    const startLoop = () => {
      if (rafIdRef.current !== null) return;
      lastTimeRef.current = null;

      const render = (timestamp: number) => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        if (lastTimeRef.current === null) {
          lastTimeRef.current = timestamp;
        }

        let deltaTime = (timestamp - lastTimeRef.current) / 1000;
        deltaTime = Math.min(deltaTime, 0.1);
        lastTimeRef.current = timestamp;

        totalTimeRef.current += deltaTime;

        drawRef.current(ctx, deltaTime, totalTimeRef.current);

        rafIdRef.current = requestAnimationFrame(render);
      };

      rafIdRef.current = requestAnimationFrame(render);
    };

    const stopLoop = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          isIntersectingRef.current = entry.isIntersecting;
          if (entry.isIntersecting) {
            startLoop();
          } else {
            stopLoop();
          }
        }
      },
      { threshold: 0.1 }
    );
    intersectionObserver.observe(canvas);

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = options?.width ?? entry.contentRect.width;
        if (newWidth !== containerWidthRef.current) {
          containerWidthRef.current = newWidth;
          updateDimensions(newWidth);
          if (isIntersectingRef.current) {
            stopLoop();
            startLoop();
          }
        }
      }
    });
    resizeObserver.observe(parent);

    return () => {
      stopLoop();
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [options?.width, options?.height, options?.pixelRatio]);

  return canvasRef;
}
